import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  fetchAddressCandidate,
  fetchNovuSubscriberInfo,
  fetchUserDetails,
  siteActiveLicenses,
  updateUserLanguage,
} from '../api/services/apiService'
import { fetchOrganizationSitesForUser, fetchUserOrganization } from '../api/services/organization.service'
import { siteService } from '../api/services/site.service'
import { userService } from '../api/services/user.service'
import {
  setAddressCandidate,
  setBootstrapError,
  setCurrentSite,
  setNovuSubscriberInfo,
  setOrganization,
  setProductLicenses,
  setReady,
  setSites,
  setUserDetails,
  setWorkingAddress,
} from '../store/authSlice'
import { useAppDispatch, useAppSelector } from '../store/store.hooks'
import { logger } from '../utils/logger'

export const useBootstrap = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const hasBootstrapped = useRef(false)

  useEffect(() => {
    if (!accessToken || hasBootstrapped.current) return
    hasBootstrapped.current = true

    const bootstrap = async () => {
      try {
        const [novuResponse, userDetailsResponse, organizationResponse] = await Promise.all([
          fetchNovuSubscriberInfo(),
          fetchUserDetails(),
          fetchUserOrganization(),
        ])

        logger.log('[Bootstrap] userDetails:', userDetailsResponse)

        const organizationSites = await fetchOrganizationSitesForUser(organizationResponse.id)
        logger.log('[Bootstrap] organizationSites:', organizationSites)

        dispatch(setSites(organizationSites))

        const allSites = organizationSites.flatMap((org) => org.sites)
        logger.log('[Bootstrap] allSites:', allSites)
        logger.log('[Bootstrap] lastVisitedSiteId:', userDetailsResponse.lastVisitedSiteId)

        let currentSite = undefined

        if (userDetailsResponse.lastVisitedSiteId) {
          const lastSite = allSites.find((s) => s.id === userDetailsResponse.lastVisitedSiteId)
          if (lastSite) {
            const lastSiteDetails = await siteService.fetchSiteDetails(lastSite.id)
            currentSite = lastSiteDetails
            dispatch(setCurrentSite(lastSiteDetails))
          } else {
            logger.log('[Bootstrap] lastVisitedSite not found, redirecting to hub')
          }
        } else if (allSites.length === 1) {
          logger.log('[Bootstrap] Single site, auto-selecting:', allSites[0])
const currentSiteDetails = await siteService.fetchSiteDetails(allSites[0].id)
currentSite = currentSiteDetails
dispatch(setCurrentSite(currentSiteDetails))
await userService.updateLastVisitedSite(allSites[0].id)
        }

        if (currentSite) {
          logger.log('[Bootstrap] Fetching product licenses for site', currentSite.id)
          const [licensesResponse, addressCandidateResponse] = await Promise.all([
            siteActiveLicenses.list(currentSite.id),
            fetchAddressCandidate(currentSite.id),
          ])
          dispatch(setProductLicenses(licensesResponse))
          dispatch(setAddressCandidate(addressCandidateResponse))
          const hostAddress = addressCandidateResponse[0]?.hostAddress
          if (hostAddress) {
            logger.log('[Bootstrap] workingAddress:', hostAddress)
            dispatch(setWorkingAddress(hostAddress))
          }
        }

        dispatch(setNovuSubscriberInfo(novuResponse))
        dispatch(setUserDetails(userDetailsResponse))
        dispatch(setOrganization(organizationResponse))

        if (!userDetailsResponse?.language) {
          i18n.changeLanguage(i18n.language)
          updateUserLanguage(i18n.language)
        } else {
          i18n.changeLanguage(userDetailsResponse.language)
        }

        dispatch(setReady(true))

        if (userDetailsResponse.lastVisitedSiteId) {
          const lastSite = allSites.find((s) => s.id === userDetailsResponse.lastVisitedSiteId)
          navigate(lastSite ? '/' : '/hub')
        } else if (allSites.length === 1) {
          navigate('/')
        } else {
          navigate('/hub')
        }
      } catch (error) {
        logger.error('[Bootstrap] Failed to fetch user data:', error)
        dispatch(setBootstrapError(true))
        dispatch(setReady(true))
      }
    }

    bootstrap()
  }, [accessToken, dispatch])
}
